"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/ui/phone-input";
import type { Value } from "react-phone-number-input";
import { formService, lookupService } from "@/lib/api/services";
import { Image as ImageIcon } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getErrorMessage } from "@/lib/utils/error-handler";
import { useAuthStore } from "@/store/auth";
import { Link } from "@/i18n/routing";
import { PageSectionEditor } from "@/components/theme-editor";

/** Shape from API: /lookups/contact-message-types (Id/Name or id/name) */
type ContactMessageType = {
  Id?: number;
  id?: number;
  Name?: string;
  name?: string;
};

export default function ContactPage() {
  const t = useTranslations();
  const tc = useTranslations("contact");
  const locale = useLocale();
  const [messageTypes, setMessageTypes] = useState<ContactMessageType[]>([]);
  const [messageTypesLoading, setMessageTypesLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const currentUser = useAuthStore((s) => s.user);

  const firstTypeId = messageTypes[0]
    ? (messageTypes[0].Id ?? messageTypes[0].id ?? 0)
    : 0;

  const isLoggedIn = Boolean(currentUser);

  const messageTypeRequiredMsg = tc("validation.messageTypeRequired", {
    defaultValue: "الرجاء اختيار نوع الرسالة",
  });

  const formSchema = z.object({
    // Guests must identify themselves; logged-in users' account info is used.
    name: isLoggedIn
      ? z.string().trim().optional()
      : z
          .string()
          .trim()
          .min(
            1,
            tc("validation.nameRequired", {
              defaultValue: "الرجاء إدخال الاسم",
            }),
          ),
    email: z
      .string()
      .trim()
      .email(
        tc("validation.emailInvalid", {
          defaultValue: "البريد الإلكتروني غير صحيح",
        }),
      )
      .optional()
      .or(z.literal("")),
    mobile: z.string().trim().optional(),
    title: z
      .string()
      .trim()
      .min(
        1,
        tc("validation.titleRequired", {
          defaultValue: "الرجاء إدخال عنوان الرسالة",
        }),
      ),
    description: z
      .string()
      .trim()
      .min(
        1,
        tc("validation.descriptionRequired", {
          defaultValue: "الرجاء إدخال نص الرسالة",
        }),
      ),
    messageTypeId: z
      .number()
      .refine((n) => n > 0, { message: messageTypeRequiredMsg }),
  });

  type ContactFormValues = z.infer<typeof formSchema>;

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      mobile: "",
      title: "",
      description: "",
      messageTypeId: 0,
    },
  });

  const currentUserMobile = (
    (currentUser?.mobile as string | undefined) ||
    (currentUser?.PhoneNumber as string | undefined) ||
    (currentUser?.phoneNumber as string | undefined) ||
    ""
  ).trim();

  useEffect(() => {
    let cancelled = false;
    setMessageTypesLoading(true);
    lookupService
      .getContactMessageTypes()
      .then((res) => {
        if (cancelled || !res.success) return;
        const data = res.data as unknown;
        const list = Array.isArray(data)
          ? data
          : ((
              data as {
                data?: ContactMessageType[];
                items?: ContactMessageType[];
              }
            )?.data ??
            (
              data as {
                data?: ContactMessageType[];
                items?: ContactMessageType[];
              }
            )?.items ??
            []);
        const types = (list as ContactMessageType[]).filter(
          (item) => (item.Id ?? item.id) != null,
        );
        if (types.length > 0) {
          setMessageTypes(types);
          const firstId = types[0].Id ?? types[0].id ?? 0;
          form.setValue("messageTypeId", firstId);
        }
        if (currentUserMobile) {
          form.setValue("mobile", currentUserMobile);
        }
      })
      .catch(() => {
        if (!cancelled) setMessageTypes([]);
      })
      .finally(() => {
        if (!cancelled) setMessageTypesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentUserMobile, form]);

  useEffect(() => {
    if (!imageFile) {
      setImagePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const onSubmit = async (values: ContactFormValues) => {
    try {
      // Requester identity: account info for logged-in users, form input for guests.
      const accountName = (
        (currentUser?.FullName as string | undefined) ||
        (currentUser?.Name as string | undefined) ||
        (currentUser?.name as string | undefined) ||
        (currentUser?.UserName as string | undefined) ||
        ""
      ).trim();
      const accountEmail = (
        (currentUser?.Email as string | undefined) ||
        (currentUser?.email as string | undefined) ||
        ""
      ).trim();
      const requesterName = isLoggedIn
        ? accountName || currentUserMobile
        : (values.name || "").trim();
      const requesterEmail = isLoggedIn ? accountEmail : (values.email || "").trim();
      const requesterMobile = values.mobile?.trim() || currentUserMobile || "";

      const selectedType = messageTypes.find(
        (item) => (item.Id ?? item.id) === values.messageTypeId,
      );
      const category =
        values.messageTypeId === 1
          ? "suggestion"
          : values.messageTypeId === 2
            ? "complaint"
            : (selectedType?.Name ?? selectedType?.name ?? "contact");

      // Primary destination: the association's CRM support tickets.
      const crmRes = await fetch("/api/crm-ticket", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          subject: values.title.trim(),
          description: values.description.trim(),
          name: requesterName || undefined,
          email: requesterEmail || undefined,
          mobile: requesterMobile || undefined,
          category,
        }),
      });
      const crmJson = (await crmRes.json().catch(() => ({}))) as {
        success?: boolean;
        message?: string;
      };
      if (!crmRes.ok || crmJson.success === false) {
        form.setError("root", {
          message: crmJson.message || tc("error"),
        });
        return;
      }

      // Secondary (best-effort): legacy contact messages so the message still
      // appears under "my messages" in the profile. Never blocks the ticket.
      try {
        let uploadedImageId: number | null = null;
        if (imageFile) {
          const uploadRes = await formService.uploadContactImage(imageFile);
          if (uploadRes.success && uploadRes.data?.imageId) {
            uploadedImageId = uploadRes.data.imageId;
          }
        }
        await formService.submitContact({
          contactMessageTypeId: values.messageTypeId,
          title: values.title.trim(),
          description: values.description.trim(),
          // Association context is sent via the X-Association-Id header (see apiClient),
          // so it must not be included in the body (backend rejects unknown properties).
          // Use authenticated user id when available to match profile message listing behavior.
          creationUser:
            (currentUser?.id as string | undefined) ||
            (currentUser?.Id as string | undefined) ||
            (currentUser?.userId as string | undefined) ||
            (currentUser?.UserId as string | undefined) ||
            (currentUser?.sub as string | undefined) ||
            requesterMobile ||
            "guest",
          mobile: requesterMobile || undefined,
          imageId: uploadedImageId,
        });
      } catch (legacyError) {
        console.warn("Legacy contact message submission failed:", legacyError);
      }

      setSubmitted(true);
      form.reset({
        name: "",
        email: "",
        mobile: "",
        title: "",
        description: "",
        messageTypeId: firstTypeId || values.messageTypeId,
      });
      setImageFile(null);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      form.setError("root", {
        message: getErrorMessage(error, tc("error"), t),
      });
    }
  };

  return (
    <PageSectionEditor section="formCard">
      <main className="min-h-screen bg-muted/30">
        <div className="content-container py-10">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h1 className="text-2xl md:text-3xl font-bold">{tc("title")}</h1>
              <div
                className="mx-auto mt-4 h-1 w-24 rounded bg-primary"
                data-te-accent=""
              />
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-border/60 p-5 md:p-8">
              {submitted ? (
                <div className="py-10 text-center">
                  <div className="text-success text-lg font-semibold mb-3">
                    ✓ {tc("success")}
                  </div>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <Button asChild variant="ghost">
                      <Link href="/my-profile/complaints">
                        {locale === "en" ? "View my messages" : "عرض رسائلي"}
                      </Link>
                    </Button>
                  </div>
                </div>
              ) : (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-4"
                  >
                    {!isLoggedIn ? (
                      <>
                        <FormField
                          control={form.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  placeholder={tc("name")}
                                  className="h-11"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input
                                  {...field}
                                  type="email"
                                  dir={locale === "ar" ? "rtl" : "ltr"}
                                  placeholder={tc("email")}
                                  className="h-11"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </>
                    ) : null}

                    <FormField
                      control={form.control}
                      name="mobile"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <PhoneInput
                              value={(field.value || "") as Value}
                              onChange={(val) => field.onChange(val ?? "")}
                              placeholder="5XXXXXXXXX"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              {...field}
                              placeholder={tc("messageTitle")}
                              className="h-11"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="messageTypeId"
                      render={({ field }) => (
                        <FormItem>
                          <Select
                            disabled={messageTypesLoading}
                            value={field.value ? String(field.value) : ""}
                            onValueChange={(val) => field.onChange(Number(val))}
                          >
                            <FormControl>
                              <SelectTrigger className="h-11">
                                <SelectValue
                                  placeholder={
                                    messageTypesLoading
                                      ? t("common.loading")
                                      : tc("messageType")
                                  }
                                />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {messageTypes.map((item) => {
                                const id = item.Id ?? item.id ?? 0;
                                const name =
                                  item.Name ??
                                  item.name ??
                                  (id === 1
                                    ? tc("typeSuggestion")
                                    : id === 2
                                      ? tc("typeComplaint")
                                      : String(id));
                                return (
                                  <SelectItem key={id} value={String(id)}>
                                    {name}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="description"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Textarea
                              {...field}
                              placeholder={tc("description")}
                              className="resize-none"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div>
                      <div className="text-sm font-medium text-foreground pt-1">
                        {tc("image")}
                      </div>
                      <div className="flex flex-col gap-2">
                        <div className="h-24 w-24 rounded-md border bg-muted/20 overflow-hidden flex items-center justify-center">
                          {imagePreviewUrl ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            <img
                              src={imagePreviewUrl}
                              alt={tc("imageAlt")}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <ImageIcon className="h-10 w-10 text-muted-foreground" />
                          )}
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            setImageFile(file);
                          }}
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="bg-red-50 text-red-600 hover:bg-red-100 w-fit"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          {tc("upload")}
                        </Button>
                      </div>
                    </div>

                    {form.formState.errors.root?.message && (
                      <p className="text-sm text-destructive">
                        {form.formState.errors.root.message}
                      </p>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting
                        ? t("common.loading")
                        : tc("send")}
                    </Button>
                  </form>
                </Form>
              )}
            </div>
          </div>
        </div>
      </main>
    </PageSectionEditor>
  );
}
